import React, { useCallback, useState } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import DocumentPreviewModal from './DocumentPreviewModal';
import { UploadedFileResponse } from '../models/responses/UploadedFileResponse';

const HEADER_COLOR = '#8a6a4a';

interface FormulaRendererProps {
    text: string;
}

const generateStableKey = (value: string, prefix: string = 'key'): string => {
    let hash = 0;
    const str = value || 'empty';

    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
    }

    return `${prefix}-${Math.abs(hash)}`;
};

const flattenNodeWithFormulas = (node: Node): { text: string; hasMath: boolean } => {
    if (node.nodeType === Node.TEXT_NODE) {
        return { text: node.textContent ?? '', hasMath: false };
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;

        if (element.dataset && element.dataset.mathText) {
            return { text: element.dataset.mathText, hasMath: true };
        }

        if (element.tagName === 'BR') {
            return { text: '\n', hasMath: false };
        }

        let text = '';
        let hasMath = false;

        element.childNodes.forEach(child => {
            const childResult = flattenNodeWithFormulas(child);
            text += childResult.text;
            hasMath = hasMath || childResult.hasMath;
        });

        if (element.tagName === 'P' || element.tagName === 'DIV') {
            text += '\n';
        }

        return { text, hasMath };
    }

    if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        let text = '';
        let hasMath = false;

        node.childNodes.forEach(child => {
            const childResult = flattenNodeWithFormulas(child);
            text += childResult.text;
            hasMath = hasMath || childResult.hasMath;
        });

        return { text, hasMath };
    }

    return { text: '', hasMath: false };
};


const FormulaRenderer: React.FC<FormulaRendererProps> = ({ text }) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewFile, setPreviewFile] = useState<UploadedFileResponse | null>(null);
    const [previewInitialPage, setPreviewInitialPage] = useState<number | null>(null);


    // Extract filename from URL
    const getFileNameFromUrl = (url: string): string => {
        try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname;
            const filename = pathname.split('/').pop() || 'Document';

            // Clean up the filename if it has no extension
            if (!filename.includes('.')) {
                return `${filename}.pdf`; // Assume PDF if no extension
            }

            return filename;
        } catch {
            return 'Document';
        }
    };


    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        setPreviewFile(null);
    };

    const getFileNameFromAnchorText = (anchorText: string): string | null => {
        const cleanText = anchorText.replace(/<[^>]*>/g, '').trim();

        if (!cleanText || cleanText.length < 2) {
            return null;
        }

        const genericTexts = /^(click here|download|link|view|open|read more|more info|document|file|here)$/i;
        if (genericTexts.test(cleanText)) {
            return null;
        }

        if (cleanText.length <= 100) {
            return cleanText;
        }

        return null;
    };

    const fixMalformedLinks = (text: string): string => {
        return text.replace(
            /<(https?:\/\/[^">]+)["']?>([^<]+)<\/a>/gi,
            '<a href="$1">$2</a>'
        );
    };

    const parsePagesFromAnchorText = (text: string): number | null => {
        // Normalize: strip HTML tags and lower-case for easier matching
        const clean = text.replace(/<[^>]*>/g, ' ').toLowerCase();

        // Match "page", "pages", "pagini", "paginile", "pg", "p."
        const m = clean.match(
            /\b(?:page|pages|pagini|pagina|paginile|pg|p\.)\s*([\d,\s\-–]+)/i
        );

        if (!m) return null;

        // Extract all individual numbers (supports "337, 338, 340-342")
        const raw = m[1];
        const numbers: number[] = [];

        raw.split(/[,\s]+/).forEach(part => {
            const range = part.match(/^(\d+)[-–](\d+)$/);
            if (range) {
                // Expand ranges like 340-342
                const start = parseInt(range[1], 10);
                const end = parseInt(range[2], 10);
                for (let i = start; i <= end; i++) numbers.push(i);
            } else if (/^\d+$/.test(part)) {
                numbers.push(parseInt(part, 10));
            }
        });

        return numbers.length ? numbers[0] : null;
    };

    const isMarkdownSeparatorRow = (line: string): boolean => {
        if (!line) {
            return false;
        }

        const trimmed = line.trim();
        if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) {
            return false;
        }

        const segments = trimmed
            .split('|')
            .map(segment => segment.trim())
            .filter(segment => segment.length > 0);

        if (!segments.length) {
            return false;
        }

        return segments.every(segment => /^[:\-\s]+$/.test(segment));
    };

    const isPlaceholderTableRow = (line: string): boolean => {
        if (!line) {
            return false;
        }

        const cells = line
            .split('|')
            .map(segment => segment.trim())
            .filter(segment => segment.length > 0);

        if (!cells.length) {
            return false;
        }

        return cells.every(cell => cell === '---' || cell === '--' || cell === '-' || /^-+$/.test(cell));
    };

    // NEW: Function to detect and fix markdown tables by ensuring header separators exist
    const normalizeMarkdownTables = (text: string): string => {
        const lines = text.split('<br/>');
        const result: string[] = [];
        let i = 0;

        while (i < lines.length) {
            const line = lines[i].trim();

            if (line.includes('|') && line.split('|').filter(segment => segment.trim()).length >= 2) {
                result.push(line);

                const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
                if (isMarkdownSeparatorRow(nextLine)) {
                    // consume provided separator line
                    i++;
                    result.push(nextLine);
                } else {
                    const cols = line.split('|').filter(segment => segment.trim()).length;
                    const separator = '|' + ' --- |'.repeat(cols);
                    result.push(separator);
                }
            } else {
                result.push(line);
            }

            i++;
        }

        return result.join('<br/>');
    };

    // NEW: Function to convert pseudo-tables (lines with dashes) into markdown tables
    const convertPseudoTables = (text: string): string => {
        const lines = text.split('<br/>');
        const result: string[] = [];
        let i = 0;

        while (i < lines.length) {
            const line = lines[i].trim();

            // Detect separator lines like | --- | --- | --- |
            if (isMarkdownSeparatorRow(line) && i > 0) {
                // Previous line should be the header
                const prevLine = lines[i - 1].trim();

                // Check if there's content after the separator (table rows)
                let hasRows = false;
                if (i + 1 < lines.length) {
                    const nextLine = lines[i + 1].trim();
                    if (
                        nextLine.startsWith('|') &&
                        nextLine.endsWith('|') &&
                        !isMarkdownSeparatorRow(nextLine)
                    ) {
                        hasRows = true;
                    }
                }

                if (hasRows) {
                    // This is a pseudo-table, convert the separator
                    const cols = line.split('|').filter(s => s.trim().length > 0).length;
                    const mdSeparator = '|' + ' --- |'.repeat(cols);
                    result[result.length - 1] = prevLine; // Keep header as-is
                    result.push(mdSeparator);
                    i++;
                    continue;
                }
            }

            result.push(line);
            i++;
        }

        return result.join('<br/>');
    };

    const detectTable = (text: string): {
        isTable: boolean;
        tableText: string;
        before: string;
        after: string;
    } => {
        const lines = text.split('<br/>');
        let tableStart = -1;
        let tableEnd = -1;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            if (!line.length) {
                continue;
            }

            const pipeStripped = line.replaceAll('|', '').trim();
            if (!pipeStripped.length) {
                continue;
            }

            const hasPipes = line.includes('|');
            const cellCount = line.split('|').filter(s => s.trim()).length;
            const isSeparator = isMarkdownSeparatorRow(line);

            const isPlaceholderRow = isPlaceholderTableRow(line);

            if (hasPipes && cellCount >= 2 && !isPlaceholderRow) {
                if (tableStart === -1) {
                    tableStart = i;
                }
                tableEnd = i;
            } else if (tableStart !== -1) {
                if (isSeparator || isPlaceholderRow) {
                    continue;
                }
                break;
            }
        }

        if (tableStart !== -1 && tableEnd !== -1 && tableEnd - tableStart >= 1) {
            const tableLines = lines.slice(tableStart, tableEnd + 1);

            const contentRows = tableLines.filter((line, idx) => {
                if (idx === 0) return false;
                if (isMarkdownSeparatorRow(line)) return false;
                return !isPlaceholderTableRow(line);
            });

            if (contentRows.length >= 1) {
                const beforeLines = lines.slice(0, tableStart);
                const afterLines = lines.slice(tableEnd + 1);

                while (afterLines.length) {
                    const trimmed = afterLines[0].trim();
                    if (!trimmed.length) {
                        afterLines.shift();
                        continue;
                    }

                    const pipeStripped = trimmed.replaceAll('|', '').trim();
                    if (!pipeStripped.length || isMarkdownSeparatorRow(trimmed) || isPlaceholderTableRow(trimmed)) {
                        afterLines.shift();
                        continue;
                    }
                    break;
                }

                return {
                    isTable: true,
                    tableText: tableLines.join('<br/>'),
                    before: beforeLines.join('<br/>'),
                    after: afterLines.join('<br/>')
                };
            }
        }

        return {
            isTable: false,
            tableText: '',
            before: '',
            after: text
        };
    };

    // NEW: Function to render markdown tables
    const renderMarkdownTable = (tableText: string, key: number): JSX.Element => {
        const rows = tableText
            .split('<br/>')
            .map(row => row.trim())
            .filter(row => row.length > 0 && row.includes('|'));

        if (rows.length < 2) {
            // Not enough rows for a proper table
            return <span key={key}>{tableText}</span>;
        }

        // Parse header
        const headerCells = rows[0]
            .split('|')
            .map(cell => cell.trim())
            .filter(cell => cell.length > 0);

        // Skip separator rows and filter out placeholder rows
        const bodyRows = rows.slice(1)
            .filter(row => !isMarkdownSeparatorRow(row))
            .map(row =>
                row.split('|')
                    .map(cell => cell.trim())
                    .filter(cell => cell.length > 0)
            )
            .filter(row => row.length > 0 && !row.every(cell => /^-+$/.test(cell) || cell === '--' || cell === '-'));

        // If no valid body rows, don't render the table
        if (bodyRows.length === 0) {
            return <span key={key}>{tableText}</span>;
        }

        const headerKeyUsage = new Map<string, number>();
        const rowKeyUsage = new Map<string, number>();
        const cellKeyUsage = new Map<string, number>();

        const getUniqueKey = (value: string, prefix: string, usageMap: Map<string, number>): string => {
            const count = usageMap.get(value) ?? 0;
            usageMap.set(value, count + 1);
            return `${generateStableKey(value, prefix)}-${count}`;
        };

        // Helper function to process cell content (handle bold, etc.)
        const processCellContent = (cellText: string): (JSX.Element | string)[] => {
            const parts: (JSX.Element | string)[] = [];
            const boldUsage = new Map<string, number>();

            cellText.split(/(\*\*.*?\*\*)/).forEach((part) => {
                if (part.startsWith('**') && part.endsWith('**')) {
                    const content = part.slice(2, -2);
                    const key = getUniqueKey(`${cellText}|${content}`, 'bold', boldUsage);
                    parts.push(
                        <strong key={key} style={{ fontWeight: 700 }}>
                            {content}
                        </strong>
                    );
                } else if (part) {
                    parts.push(part);
                }
            });

            return parts.length > 0 ? parts : [cellText];
        };

        return (
            <div key={key} style={{ overflowX: 'auto', margin: '1.5rem 0' }}>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.95em',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}>
                    <thead>
                        <tr style={{
                            backgroundColor: HEADER_COLOR,
                            color: 'white',
                            textAlign: 'left',
                            fontWeight: 600
                        }}>
                            {headerCells.map((cell) => {
                                const key = getUniqueKey(cell, 'header', headerKeyUsage);
                                return (
                                    <th key={key} style={{
                                        padding: '12px 15px',
                                        borderBottom: `2px solid ${HEADER_COLOR}`
                                    }}>
                                        {processCellContent(cell)}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {bodyRows.map((row, rowIdx) => {
                            const rowSignature = row.join('|');
                            const rowKey = getUniqueKey(rowSignature, 'row', rowKeyUsage);

                            return (
                                <tr key={rowKey} style={{
                                    backgroundColor: rowIdx % 2 === 0 ? '#ffffff' : '#f9fafb',
                                    borderBottom: '1px solid #e5e7eb'
                                }}>
                                    {row.map((cell) => {
                                        const cellSignature = `${rowSignature}|${cell}`;
                                        const cellKey = getUniqueKey(cellSignature, 'cell', cellKeyUsage);
                                        return (
                                            <td key={cellKey} style={{
                                                padding: '12px 15px',
                                                color: '#374151'
                                            }}>
                                                {processCellContent(cell)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const processHtmlLinks = (textSegment: string): (JSX.Element | string)[] => {
        const parts: (JSX.Element | string)[] = [];

        // Regex to match <a> tags with href attribute
        const linkRegex = /<a\s+([^>]*\s+)?href\s*=\s*["']([^"']+)["']([^>]*)>(.*?)<\/a>/gi;
        let lastIndex = 0;
        let match;

        // Reset regex lastIndex
        linkRegex.lastIndex = 0;

        while ((match = linkRegex.exec(textSegment)) !== null) {
            if (match.index > lastIndex) {
                parts.push(textSegment.slice(lastIndex, match.index));
            }

            // Extract link components
            const originalUrl = match[2]; // href value
            const linkText = match[4]; // Link content

            // Check for page numbers after the closing </a> tag
            const afterLinkText = textSegment.slice(match.index + match[0].length);
            const afterLinkMatch = afterLinkText.match(/^\s*(?:page|pages|pagini|pagina|pg|p\.)\s+([\d,\s\-–]+)/i);

            // Parse the link text to separate filename and page numbers
            const pageParts = linkText.match(/^(.*?)\s+(?:page|pages|pagini|pagina|pg|p\.)\s+([\d,\s\-–]+)$/i);

            if (pageParts || afterLinkMatch) {
                // We have a filename + page number(s)
                const fileName = pageParts ? pageParts[1].trim() : linkText.trim();
                const pageText = pageParts ? pageParts[2].trim() : (afterLinkMatch ? afterLinkMatch[1].trim() : '');

                // Handle click event for filename
                const handleFileClick = (e: React.MouseEvent, url: string, pageNum?: number) => {
                    e.preventDefault();

                    if (url.startsWith('mailto:') || url.startsWith('tel:')) {
                        window.location.href = url;
                        return;
                    }

                    const fileResponse: UploadedFileResponse = {
                        Id: 0,
                        FileName: fileName || getFileNameFromUrl(url),
                        Url: url,
                        DateUploadedUtc: new Date(),
                        DateCompletedUtc: new Date(),
                        Status: 1
                    };

                    setPreviewFile(fileResponse);
                    setPreviewInitialPage(pageNum ?? null);
                    setIsPreviewOpen(true);
                };

                // Parse page ranges and individual pages
                const pageElements: JSX.Element[] = [];
                const pageSegments = pageText.split(',').map(s => s.trim());

                // Extract the first page number for the filename link
                let firstPageNumber: number | undefined = undefined;
                if (pageSegments.length > 0) {
                    const firstSegment = pageSegments[0];
                    const rangeMatch = firstSegment.match(/^(\d+)[-–](\d+)$/);
                    if (rangeMatch) {
                        firstPageNumber = parseInt(rangeMatch[1], 10);
                    } else if (/^\d+$/.test(firstSegment)) {
                        firstPageNumber = parseInt(firstSegment, 10);
                    }
                }

                pageSegments.forEach((segment, segmentIdx) => {
                    if (segmentIdx > 0) {
                        pageElements.push(<span key={`comma-${segmentIdx}`}>, </span>);
                    }

                    const rangeMatch = segment.match(/^(\d+)[-–](\d+)$/);
                    if (rangeMatch) {
                        // This is a range like "121-125"
                        const startPage = parseInt(rangeMatch[1], 10);
                        const endPage = parseInt(rangeMatch[2], 10);

                        pageElements.push(
                            <a
                                key={`page-start-${segmentIdx}`}
                                href="#"
                                onClick={(e) => handleFileClick(e, originalUrl, startPage)}
                                style={{
                                    color: '#0066cc',
                                    textDecoration: 'none',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                                title={`Open at page ${startPage}`}
                            >
                                {startPage}
                            </a>
                        );

                        pageElements.push(<span key={`dash-${segmentIdx}`}>-</span>);

                        pageElements.push(
                            <a
                                key={`page-end-${segmentIdx}`}
                                href="#"
                                onClick={(e) => handleFileClick(e, originalUrl, endPage)}
                                style={{
                                    color: '#0066cc',
                                    textDecoration: 'none',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                                title={`Open at page ${endPage}`}
                            >
                                {endPage}
                            </a>
                        );
                    } else if (/^\d+$/.test(segment)) {
                        // Single page number
                        const pageNum = parseInt(segment, 10);
                        pageElements.push(
                            <a
                                key={`page-${segmentIdx}`}
                                href="#"
                                onClick={(e) => handleFileClick(e, originalUrl, pageNum)}
                                style={{
                                    color: '#0066cc',
                                    textDecoration: 'none',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                                title={`Open at page ${pageNum}`}
                            >
                                {pageNum}
                            </a>
                        );
                    } else {
                        // Invalid format, just display as text
                        pageElements.push(<span key={`text-${segmentIdx}`}>{segment}</span>);
                    }
                });

                // Create the complete link structure
                parts.push(
                    <span key={`link-${match.index}`}>
                        <a
                            href="#"
                            onClick={(e) => handleFileClick(e, originalUrl, firstPageNumber)}
                            style={{
                                color: '#0066cc',
                                textDecoration: 'none',
                                fontWeight: '500',
                                cursor: 'pointer'
                            }}
                            title={firstPageNumber ? `Click to preview at page ${firstPageNumber}` : `Click to preview: ${originalUrl}`}
                        >
                            {fileName}
                        </a>
                        {' page '}
                        {pageElements}
                    </span>
                );

                // If we matched pages after the link, skip them in the next iteration
                if (afterLinkMatch && !pageParts) {
                    lastIndex = match.index + match[0].length + afterLinkMatch[0].length;
                    continue;
                }
            } else {
                // No page numbers detected, handle as before
                const handleLinkClick = (e: React.MouseEvent, url: string) => {
                    e.preventDefault();

                    if (url.startsWith('mailto:') || url.startsWith('tel:')) {
                        window.location.href = url;
                        return;
                    }

                    const fileName = getFileNameFromAnchorText(linkText) || getFileNameFromUrl(url);

                    const fileResponse: UploadedFileResponse = {
                        Id: 0,
                        FileName: fileName,
                        Url: url,
                        DateUploadedUtc: new Date(),
                        DateCompletedUtc: new Date(),
                        Status: 1
                    };

                    const pageFromText = parsePagesFromAnchorText(linkText);
                    const page1Based = pageFromText ?? null;

                    setPreviewFile(fileResponse);
                    setPreviewInitialPage(page1Based);
                    setIsPreviewOpen(true);
                };

                parts.push(
                    <a
                        key={`link-${match.index}`}
                        href="#"
                        onClick={(e) => handleLinkClick(e, originalUrl)}
                        style={{
                            color: '#0066cc',
                            textDecoration: 'none',
                            fontWeight: '500',
                            cursor: 'pointer'
                        }}
                        title={`Click to preview: ${originalUrl}`}
                        dangerouslySetInnerHTML={{ __html: linkText }}
                    />
                );
            }

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < textSegment.length) {
            parts.push(textSegment.slice(lastIndex));
        }

        return parts.length > 0 ? parts : [textSegment];
    };

    // Function to convert leading dashes to bullet points
    const convertDashesToBullets = (text: string): string => {
        const lines = text.split('<br/>');
        return lines.map(line => {
            // Match lines that start with "- " (dash followed by space)
            if (line.trim().match(/^-\s+/)) {
                return line.replace(/^(\s*)-\s+/, '$1• ');
            }
            return line;
        }).join('<br/>');
    };


    const FORMULA_TOKEN_MAX_LENGTH = 12;
    const formulaCharRegex = /^[A-Za-z0-9=+\-*/⋅∙×÷√∑∏±<>°φΔΩλµκθρστυαβγδεζηιξπχωψ()]+$/i;
    const formulaSymbolRegex = /[=+\-*/⋅∙×÷√∑∏±<>()φΔΩ°π]/;

    const isFormulaToken = (value: string): boolean => {
        if (!value) {
            return false;
        }

        const normalized = value.replace(/&nbsp;/gi, ' ').trim();

        if (!normalized || normalized.length > FORMULA_TOKEN_MAX_LENGTH) {
            return false;
        }

        if (normalized.includes(' ')) {
            return false;
        }

        if (/^[•\-]/.test(normalized)) {
            return false;
        }

        if (!formulaCharRegex.test(normalized)) {
            return false;
        }

        if (normalized.length === 1) {
            return true;
        }

        if (/^\d+$/.test(normalized)) {
            return true;
        }

        if (formulaSymbolRegex.test(normalized)) {
            return true;
        }

        if (/^[A-Za-z]+$/.test(normalized) && normalized.length <= 2) {
            return true;
        }

        return false;
    };

    const mergeBrokenFormulaLines = (text: string): string => {
        const segments = text.split('<br/>');
        const merged: string[] = [];
        let buffer: { raw: string; trimmed: string }[] = [];
        const MIN_SEGMENTS = 4;

        const flushBuffer = () => {
            if (!buffer.length) {
                return;
            }

            const hasSymbols = buffer.some(({ trimmed }) => formulaSymbolRegex.test(trimmed));

            if (buffer.length >= MIN_SEGMENTS && hasSymbols) {
                merged.push(buffer.map(({ trimmed }) => trimmed).join(''));
            } else {
                buffer.forEach(({ raw }) => merged.push(raw));
            }

            buffer = [];
        };

        for (const segment of segments) {
            const trimmed = segment.trim();

            if (isFormulaToken(trimmed)) {
                buffer.push({ raw: segment, trimmed });
                continue;
            }

            flushBuffer();
            merged.push(segment);
        }

        flushBuffer();

        return merged.join('<br/>');
    };

    const subscriptMap: Record<string, string> = {
        '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄',
        '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
        '+': '₊', '-': '₋', '=': '₌', '(': '₍', ')': '₎'
    };

    const superscriptMap: Record<string, string> = {
        '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
        '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
        '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾'
    };

    const toSubscript = (value: string): string => (
        value.split('').map((char) => subscriptMap[char] ?? char).join('')
    );

    const toSuperscript = (value: string): string => (
        value.split('').map((char) => superscriptMap[char] ?? char).join('')
    );

    const formatLatexForClipboard = (latex: string): string => {
        let result = latex;

        result = result.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, (_, numerator, denominator) => `(${numerator})/(${denominator})`);
        result = result.replace(/\\sqrt\s*\{([^}]*)\}/g, (_, radicand) => `√(${radicand})`);
        result = result.replace(/\\cdot/g, '⋅');
        result = result.replace(/\\times/g, '×');
        result = result.replace(/\\pi/g, 'π');
        result = result.replace(/\\varphi|\\phi/g, 'φ');
        result = result.replace(/\\theta/g, 'θ');
        result = result.replace(/\\Delta/g, 'Δ');
        result = result.replace(/\\left\s*/g, '');
        result = result.replace(/\\right\s*/g, '');
        result = result.replace(/\\cos/g, 'cos');
        result = result.replace(/\\sin/g, 'sin');
        result = result.replace(/\\tan/g, 'tan');

        result = result.replace(/_\{([^}]*)\}/g, (_, sub) => toSubscript(sub));
        result = result.replace(/_(\d+)/g, (_, digits) => toSubscript(digits));
        result = result.replace(/\^\{([^}]*)\}/g, (_, sup) => toSuperscript(sup));
        result = result.replace(/\^(\d+)/g, (_, digits) => toSuperscript(digits));

        result = result.replace(/\\,/g, ' ');
        result = result.replace(/\\!/g, '');
        result = result.replace(/\\;/g, ' ');
        result = result.replace(/\\\\/g, ' ');

        result = result.replace(/\\/g, '');
        result = result.replace(/\s+/g, ' ');

        return result.trim();
    };

    const getMathText = (latex: string): string => formatLatexForClipboard(latex);

    const handleCopy = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }

        let aggregatedText = '';
        let containsMath = false;

        for (let i = 0; i < selection.rangeCount; i++) {
            const range = selection.getRangeAt(i);
            const fragment = range.cloneContents();
            const { text, hasMath } = flattenNodeWithFormulas(fragment);
            aggregatedText += text;
            containsMath = containsMath || hasMath;
        }

        if (!containsMath) {
            return;
        }

        event.preventDefault();
        const normalized = aggregatedText
            .replace(/\u00a0/g, ' ')
            .replace(/[ \t]+\n/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();

        event.clipboardData?.setData('text/plain', normalized);
    }, []);


    const renderInlineContent = (text: string, keyPrefix: string = 'il', bold: boolean = false): (JSX.Element | string)[] => {
        // Handle inline math in text: \(...\), $...$, and bare X_{...} / X^{...} patterns.
        // This runs AFTER bold markers are already resolved, so it never splits **...** pairs.
        const inlineRegex = /\\\([\s\S]*?\\\)|\$([^\s$][^$]*?[^\s$]|\S)\$|[A-Za-z][A-Za-z0-9]*(?:_\{[^}]+\}|\^\{[^}]+\})+/g;
        const result: (JSX.Element | string)[] = [];
        let lastIdx = 0;
        let m: RegExpExecArray | null;

        inlineRegex.lastIndex = 0;
        while ((m = inlineRegex.exec(text)) !== null) {
            if (m.index > lastIdx) {
                result.push(...processHtmlLinks(text.slice(lastIdx, m.index)));
            }

            const matched = m[0];
            let mathStr: string;

            if (matched.startsWith('\\(') && matched.endsWith('\\)')) {
                mathStr = matched.slice(2, -2);
            } else if (matched.startsWith('$') && matched.endsWith('$')) {
                mathStr = matched.slice(1, -1);
            } else {
                mathStr = matched; // bare X_{...} — valid KaTeX
            }

            mathStr = mathStr.replace(/<br\s*\/?>/g, '').trim();
            if (bold) {
                mathStr = `\\boldsymbol{${mathStr}}`;
            }

            try {
                result.push(
                    <span
                        key={`${keyPrefix}-im-${m.index}`}
                        style={{ display: 'inline-block' }}
                        data-math-text={getMathText(mathStr)}
                    >
                        <InlineMath math={mathStr} />
                    </span>
                );
            } catch {
                result.push(matched);
            }

            lastIdx = m.index + matched.length;
        }

        if (lastIdx < text.length) {
            result.push(...processHtmlLinks(text.slice(lastIdx)));
        }

        return result.length > 0 ? result : processHtmlLinks(text);
    };

    const processInlineMarkdown = (textSegment: string): (JSX.Element | string)[] => {
        return textSegment.split(/(\*\*.*?\*\*)/).map((textPart: string, textIndex: number) => {
            if (textPart.startsWith('**') && textPart.endsWith('**')) {
                const boldContent = textPart.slice(2, -2);
                const linkParts = renderInlineContent(boldContent, `pim-bold-${textIndex}`, true);
                return (
                    <strong
                        key={`bold-${textIndex}`}
                        style={{
                            fontWeight: 700,
                            color: '#1a1a1a'
                        }}
                    >
                        {linkParts.map((linkPart: JSX.Element | string, linkIndex: number) => (
                            <React.Fragment key={linkIndex}>
                                {linkPart}
                            </React.Fragment>
                        ))}
                    </strong>
                );
            }

            const linkParts = renderInlineContent(textPart, `pim-text-${textIndex}`);
            return (
                <React.Fragment key={`text-${textIndex}`}>
                    {linkParts.map((linkPart: JSX.Element | string, linkIndex: number) => (
                        <React.Fragment key={linkIndex}>
                            {linkPart}
                        </React.Fragment>
                    ))}
                </React.Fragment>
            );
        });
    };

    const processMarkdownHeaders = (text: string): (JSX.Element | string)[] => {
        const parts: (JSX.Element | string)[] = [];
        const segments = text.split('<br/>');

        segments.forEach((segment, index) => {
            const hasPriorContent = segments.slice(0, index).some((previousSegment) => previousSegment.trim().length > 0);
            const hasPrecedingEmptyLine = index > 0 && segments[index - 1].trim() === '';

            if (index > 0) {
                parts.push(<br key={`br-${index}`} />);
            }

            const blockquoteMatch = segment.match(/^>\s*(.*)/);
            if (blockquoteMatch) {
                parts.push(
                    <blockquote key={`bq-${index}`} style={{
                        borderLeft: `4px solid ${HEADER_COLOR}`,
                        backgroundColor: '#f0f9f4',
                        margin: '0.25rem 0',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '0 4px 4px 0',
                        fontStyle: 'italic',
                        color: '#374151',
                    }}>
                        {processInlineMarkdown(blockquoteMatch[1])}
                    </blockquote>
                );
                return;
            }

            const fourthLevelHeaderMatch = segment.match(/^(#{4})\s+(.+?)\s*$/);
            if (fourthLevelHeaderMatch) {
                const content = fourthLevelHeaderMatch[2];
                parts.push(
                    <h4 key={`h4-${index}`} style={{
                        fontSize: '1.08em',
                        fontWeight: 700,
                        color: HEADER_COLOR,
                        marginTop: hasPriorContent && !hasPrecedingEmptyLine ? '0.5rem' : '0',
                        marginBottom: '0',
                        lineHeight: '1.4'
                    }}>
                        {processInlineMarkdown(content)}
                    </h4>
                );
                return;
            }

            // Check for markdown headers (#, ##, ###)
            const headerMatch = segment.match(/^(#{1,3})\s+(.+?)(?:\s+[📘⚡🔹✅⚠️💡🔧📊])?$/);

            if (headerMatch) {
                const level = headerMatch[1].length;
                const content = headerMatch[2];

                if (level === 1) {
                    parts.push(
                        <h1 key={`h1-${index}`} style={{
                            fontSize: '1.75em',
                            fontWeight: 700,
                            color: HEADER_COLOR,
                            marginTop: index === 0 || hasPrecedingEmptyLine ? '0' : '1.25rem',
                            marginBottom: '-0.35rem',
                            lineHeight: '1.25'
                        }}>
                            {processInlineMarkdown(content)}
                        </h1>
                    );
                } else if (level === 2) {
                    parts.push(
                        <h2 key={`h2-${index}`} style={{
                            fontSize: '1.5em',
                            fontWeight: 700,
                            color: HEADER_COLOR,
                            marginTop: hasPriorContent && !hasPrecedingEmptyLine ? '0.75rem' : '0',
                            marginBottom: '-0.35rem',
                            lineHeight: '1.3'
                        }}>
                            {processInlineMarkdown(content)}
                        </h2>
                    );
                } else if (level === 3) {
                    parts.push(
                        <h3 key={`h3-${index}`} style={{
                            fontSize: '1.25em',
                            fontWeight: 600,
                            color: HEADER_COLOR,
                            marginTop: hasPriorContent && !hasPrecedingEmptyLine ? '0.75rem' : '0',
                            marginBottom: '-0.3rem',
                            lineHeight: '1.4'
                        }}>
                            {processInlineMarkdown(content)}
                        </h3>
                    );
                }
            } else {
                parts.push(segment);
            }
        });

        return parts;
    };

    const limitConsecutiveBreaks = (text: string): string => {
        // Replace 3 or more consecutive <br/> tags with exactly 2
        return text.replace(/(<br\/>){3,}/g, '<br/><br/>');
    };

    const removeUnwantedSymbols = (text: string): string => {
        // Remove unwanted geometric symbols
        return text
            .replace(/◆/g, '')  // Remove black diamond
            .replace(/◇/g, '')  // Remove white diamond
            .replace(/♦/g, '')  // Remove diamond suit
            .replace(/♢/g, '')  // Remove white diamond suit
            .replace(/◊/g, '')  // Remove lozenge
            .replace(/\s{2,}/g, ' ')  // Clean up double spaces
            .replace(/�/g, '')  // Remove unwanted symbol
            .replace(/n\+\+/g, '')  // Remove literal "n++" sequences that creep in from OCR
            .trim();

    };

    const cleanupHeaderSpacing = (text: string): string => {
        return text
            // Remove <br/> tags immediately before headers (up to 2)
            .replaceAll(/(<br\/>){1,2}(\s*)(#{2,3}\s+)/g, '<br/>$2$3')
            // Remove <br/> tags immediately after headers (up to 2)
            .replaceAll(/(#{1,3}\s+[^\n<]+)(<br\/>){1,2}/g, '$1<br/>');
    };

    const cleanupBulletSpacing = (text: string): string => {
        return text
            // Remove excessive <br/> tags before bullet points (reduce to just one)
            .replaceAll(/(<br\/>){2,}(\s*)(•\s+)/g, '<br/>$2$3')
            // Remove excessive <br/> tags between consecutive bullet points
            .replaceAll(/(•\s+[^\n<]+)(<br\/>){2,}(\s*•\s+)/g, '$1<br/>$3');
    };

    const normalizeFormulaDelimiters = (text: string): string => {
        const lines = text.split('<br/>');
        const result: string[] = [];

        const standaloneFormulaPattern = /^(.*?(?:formula|formulan|calcul|se calculează cu|calculate with|equation)[:\s]+)\\\((.*?)\\\)(.*)$/i;
        const soloInlinePattern = /^\s*\\\((.*?)\\\)\s*$/;

        for (const line of lines) {
            const standaloneMatch = standaloneFormulaPattern.exec(line);

            if (standaloneMatch) {
                const [, prefix, formula, suffix] = standaloneMatch;

                // Check if the formula is substantial (not just a simple variable)
                const isSubstantialFormula = formula.length > 10 ||
                    formula.includes(String.raw`\frac`) ||
                    formula.includes(String.raw`\cdot`) ||
                    formula.includes(String.raw`\times`) ||
                    formula.includes('=');

                // Check if formula is at the end of the line or followed by minimal text
                const minimalSuffix = suffix.trim().length < 5;

                if (isSubstantialFormula && minimalSuffix) {
                    // Convert to block formula
                    result.push(
                        prefix.trim(),
                        String.raw`\[${formula}\]`,
                        ...(suffix.trim() ? [suffix.trim()] : [])
                    );
                    continue;
                }
            }

            // Also check for inline formulas on their own line
            const soloMatch = soloInlinePattern.exec(line);

            if (soloMatch) {
                const formula = soloMatch[1];

                // Convert substantial solo inline formulas to block
                const isSubstantial = formula.length > 10 ||
                    formula.includes('=') ||
                    formula.includes(String.raw`\frac`) ||
                    formula.includes(String.raw`\cdot`) ||
                    formula.includes(String.raw`\times`);

                if (isSubstantial) {
                    result.push(String.raw`\[${formula}\]`);
                    continue;
                }
            }

            result.push(line);
        }

        return result.join('<br/>');
    };

    const renderContent = (): JSX.Element => {
        // First, clean up malformed <br/> tags with spaces
        let cleanedText: string = text
            .replace(/< *br *\/ *>/g, '<br/>') // Fix malformed br tags
            .replace(/\[CHATGPT_LOADING\]/g, '') // Remove loading marker
            .replace(/^---\s*$/gm, '') // Remove standalone triple dashes
            .replace(/^---<br\/>$/gm, '') // Remove triple dashes with br
            .replace(/<br\/>---<br\/>/g, '<br/>') // Remove triple dashes between br tags
            .trim();

        cleanedText = cleanedText
            .replace(/\u200b/g, '') // remove zero-width spaces
            .replace(/\r?\n/g, '<br/>');
        cleanedText = fixMalformedLinks(cleanedText);
        cleanedText = convertDashesToBullets(cleanedText);
        cleanedText = normalizeFormulaDelimiters(cleanedText);
        cleanedText = cleanupHeaderSpacing(cleanedText);
        cleanedText = cleanupBulletSpacing(cleanedText);

        // Clean up malformed table summaries (like "| Parametru | Valoare pentru LEA 110 kV |")
        // that appear after proper tables - these are often just text summaries, not tables
        const lines = cleanedText.split('<br/>');
        const cleanedLines: string[] = [];
        let inMalformedTableSummary = false;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            // Detect start of malformed table summary
            if (!isMarkdownSeparatorRow(line) && !isPlaceholderTableRow(line) &&
                (line.match(/^\| *Parametr[ui]? *\|/i) ||
                    (line.match(/^\| *[^|]+ *\| *[^|]+ *\|$/) &&
                        !line.match(/\d/) && // No numbers
                        line.length < 80))) { // Short lines are likely headers

                // Check if this is really a table or just a summary line
                const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
                const hasProperSeparator = nextLine.match(/^\|[\s:-]+\|/);

                if (!hasProperSeparator) {
                    inMalformedTableSummary = true;
                }
            }

            // If we're in a malformed summary, skip lines that look like table rows
            if (inMalformedTableSummary && line.startsWith('|') && line.endsWith('|')) {
                // Check if next line ends the summary
                const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
                if (!nextLine.startsWith('|')) {
                    inMalformedTableSummary = false;
                }
                continue; // Skip this line
            } else {
                inMalformedTableSummary = false;
                cleanedLines.push(lines[i]);
            }
        }

        cleanedText = cleanedLines.join('<br/>');
        cleanedText = mergeBrokenFormulaLines(cleanedText);

        // NEW: Convert pseudo-tables first
        cleanedText = convertPseudoTables(cleanedText);
        cleanedText = limitConsecutiveBreaks(cleanedText);

        // NEW: Normalize markdown tables
        cleanedText = normalizeMarkdownTables(cleanedText);
        cleanedText = removeUnwantedSymbols(cleanedText);

        // Convert $$...$$ block math notation to \[...\] for the block extractor below
        cleanedText = cleanedText.replace(/\$\$([\s\S]*?)\$\$/g, (_, math) => `\\[${math}\\]`);

        const parts: (JSX.Element | string)[] = [];
        let currentIndex: number = 0;

        const latexRegex = /\\[[(][\s\S]*?\\[\])]/g;
        let match: RegExpExecArray | null;

        while ((match = latexRegex.exec(cleanedText)) !== null) {
            if (match.index > currentIndex) {
                const beforeText: string = cleanedText.slice(currentIndex, match.index);
                parts.push(beforeText);
            }

            const latexContent: string = match[0];
            if (latexContent.startsWith('\\[') && latexContent.endsWith('\\]')) {
                const math: string = latexContent.slice(2, -2)
                    .replace(/<br\s*\/?>/g, '')
                    .replace(/< *br *\/ *>/g, '')
                    .trim();
                try {
                    parts.push(
                        <div key={match.index} style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderLeft: `4px solid ${HEADER_COLOR}`,
                            borderRadius: '6px',
                            padding: '16px',
                            margin: '12px 0',
                            textAlign: 'center'
                        }}
                            data-math-text={getMathText(math)}
                        >
                            <BlockMath math={math} />
                        </div>
                    );
                } catch (e) {
                    console.log("LaTeX block parsing failed:", math, e);
                    parts.push(latexContent);
                }
            } else if (latexContent.startsWith('\\(') && latexContent.endsWith('\\)')) {
                const math: string = latexContent.slice(2, -2)
                    .replace(/<br\s*\/?>/g, '')
                    .replace(/< *br *\/ *>/g, '')
                    .trim();
                try {
                    parts.push(
                        <span
                            key={match.index}
                            style={{ display: 'inline-block' }}
                            data-math-text={getMathText(math)}
                        >
                            <InlineMath math={math} />
                        </span>
                    );
                } catch (e) {
                    console.log("LaTeX inline parsing failed:", math, e);
                    parts.push(latexContent);
                }
            } else {
                parts.push(latexContent);
            }

            currentIndex = match.index + match[0].length;
        }

        if (currentIndex < cleanedText.length) {
            const remainingText: string = cleanedText.slice(currentIndex);
            parts.push(remainingText);
        }

        let tableRenderCounter = 0;

        const renderHeaderSegments = (segments: (JSX.Element | string)[], keyPrefix: string): JSX.Element[] => {
            const rendered: JSX.Element[] = [];
            const keyUsage = new Map<string, number>();

            const getUniqueKey = (value: string, prefix: string): string => {
                const baseKey = generateStableKey(value, prefix);
                const count = keyUsage.get(baseKey) ?? 0;
                keyUsage.set(baseKey, count + 1);
                return `${baseKey}-${count}`;
            };

            segments.forEach((segment) => {
                if (typeof segment === 'string') {
                    segment.split(/(\*\*.*?\*\*)/).forEach((textPart: string) => {
                        if (!textPart) {
                            return;
                        }
                        const baseKey = getUniqueKey(textPart, keyPrefix);
                        if (textPart.startsWith('**') && textPart.endsWith('**')) {
                            const boldContent: string = textPart.slice(2, -2);
                            const linkParts: (JSX.Element | string)[] = renderInlineContent(boldContent, `${keyPrefix}-bold`, true);
                            rendered.push(
                                <strong
                                    key={baseKey}
                                    style={{
                                        fontWeight: 700,
                                        color: '#1a1a1a'
                                    }}
                                >
                                    {linkParts.map((linkPart: JSX.Element | string, linkIndex: number) => (
                                        <React.Fragment key={`${baseKey}-link-${linkIndex}`}>
                                            {linkPart}
                                        </React.Fragment>
                                    ))}
                                </strong>
                            );
                        } else {
                            const linkParts: (JSX.Element | string)[] = renderInlineContent(textPart, `${keyPrefix}-text`);
                            rendered.push(
                                <React.Fragment key={baseKey}>
                                    {linkParts.map((linkPart: JSX.Element | string, linkIndex: number) => (
                                        <React.Fragment key={`${baseKey}-link-${linkIndex}`}>
                                            {linkPart}
                                        </React.Fragment>
                                    ))}
                                </React.Fragment>
                            );
                        }
                    });
                } else {
                    const segKey = getUniqueKey(segment.toString(), keyPrefix);
                    rendered.push(
                        <React.Fragment key={segKey}>
                            {segment}
                        </React.Fragment>
                    );
                }
            });

            return rendered;
        };

        const renderTextContent = (text: string, keyPrefix: string): JSX.Element[] => {
            if (!text?.trim()) {
                return [];
            }

            const nodes: JSX.Element[] = [];
            const tableDetection = detectTable(text);

            if (tableDetection.isTable) {
                if (tableDetection.before && tableDetection.before.trim()) {
                    nodes.push(...renderTextContent(tableDetection.before, `${keyPrefix}-before`));
                }

                nodes.push(renderMarkdownTable(tableDetection.tableText, tableRenderCounter++));

                if (tableDetection.after && tableDetection.after.trim()) {
                    nodes.push(...renderTextContent(tableDetection.after, `${keyPrefix}-after`));
                }

                return nodes;
            }

            const headerProcessed: (JSX.Element | string)[] = processMarkdownHeaders(text);
            nodes.push(
                <span key={`${keyPrefix}-text`}>
                    {renderHeaderSegments(headerProcessed, keyPrefix)}
                </span>
            );

            return nodes;
        };

        // Process all parts for line breaks and bold formatting
        return (
            <div style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#374151'
            }}
                onCopy={handleCopy}
            >
                {parts.map((part: JSX.Element | string, index: number) => {
                    if (typeof part === 'string') {
                        // Trim leading/trailing <br/> tags
                        const trimmedPart = part
                            .replace(/^(<br\/>)+/, '')
                            .replace(/(<br\/>)+$/, '');

                        const renderedNodes = renderTextContent(trimmedPart, `part-${index}`);
                        if (!renderedNodes.length) {
                            return null;
                        }
                        return (
                            <React.Fragment key={`text-part-${index}`}>
                                {renderedNodes}
                            </React.Fragment>
                        );
                    }
                    return part;
                })}

                <DocumentPreviewModal
                    file={previewFile}
                    isOpen={isPreviewOpen}
                    onClose={handleClosePreview}
                    initialPage={previewInitialPage !== null ? Math.max(0, previewInitialPage - 1) : undefined}
                />
            </div>
        );
    };

    return renderContent();
};

export default FormulaRenderer;